import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  Divider,
  List,
  ListItem,
  ListItemContent
} from '@mui/joy';
import {
  ArrowsClockwise as SyncIcon,
  CheckCircle as CheckCircleIcon,
  XCircle as ErrorIcon,
  Clock as ScheduleIcon,
  Eye as PreviewIcon,
  CloudArrowUp as CloudUploadIcon
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { YextListingStatus, YextPreview, YextListing } from '../../types/marketing';

interface YextListingsCardProps {
  isAdmin?: boolean;
}

export const YextListingsCard: React.FC<YextListingsCardProps> = ({ isAdmin = false }) => {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const queryClient = useQueryClient();

  // Fetch Yext listing status
  const { data: status, isLoading: statusLoading } = useQuery<YextListingStatus>({
    queryKey: ['yext-status'],
    queryFn: async () => {
      const response = await api.get<YextListingStatus>('/api/marketing/yext-listings/status/');
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });

  // Fetch Yext preview
  const { data: preview, isLoading: previewLoading } = useQuery<YextPreview>({
    queryKey: ['yext-preview'],
    queryFn: async () => {
      const response = await api.get<YextPreview>('/api/marketing/yext-listings/preview/');
      return response.data;
    },
    enabled: showPreviewModal,
  });

  // Create listing mutation
  const createListingMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<YextListing>('/api/marketing/yext-listings/', {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['yext-status'] });
    },
  });

  // Submit to Yext mutation
  const submitMutation = useMutation({
    mutationFn: async (listingId: number) => {
      const response = await api.post(`/api/marketing/yext-listings/${listingId}/submit/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['yext-status'] });
      setShowConfirmModal(false);
    },
    onError: (error: any) => {
      console.error('Yext submit error:', error);
    },
  });

  const handleSync = async () => {
    if (!status?.has_listing) {
      // First create a listing config
      const listing = await createListingMutation.mutateAsync();
      // Then submit it
      submitMutation.mutate(listing.id);
    } else if (status?.listing_id) {
      submitMutation.mutate(status.listing_id);
    }
    setShowConfirmModal(false);
  };

  const getStatusChip = () => {
    if (!status?.has_listing) {
      return <Chip color="neutral" variant="soft" startDecorator={<ScheduleIcon />}>Not Set Up</Chip>;
    }

    switch (status.status) {
      case 'synced':
        return <Chip color="success" variant="soft" startDecorator={<CheckCircleIcon />}>Synced</Chip>;
      case 'error':
        return <Chip color="danger" variant="soft" startDecorator={<ErrorIcon />}>Error</Chip>;
      case 'needs_update':
        return <Chip color="warning" variant="soft" startDecorator={<SyncIcon />}>Needs Update</Chip>;
      case 'pending':
      default:
        return <Chip color="neutral" variant="soft" startDecorator={<ScheduleIcon />}>Pending</Chip>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Don't show if not admin
  if (!isAdmin) {
    return null;
  }

  if (statusLoading) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size="sm" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography level="h4" sx={{ mb: 0.5 }}>
                Yext Listings Distribution
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Sync your business information to 70+ directories via Yext
              </Typography>
            </Box>
            {getStatusChip()}
          </Box>

          {status?.last_sync_error && status.status === 'error' && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {status.last_sync_error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                Last Synced
              </Typography>
              <Typography level="body-md">
                {formatDate(status?.last_synced || null)}
              </Typography>
            </Box>
            {status?.yext_entity_id && (
              <Box>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  Yext Entity ID
                </Typography>
                <Typography level="body-md" sx={{ fontFamily: 'monospace', fontSize: 'sm' }}>
                  {status.yext_entity_id}
                </Typography>
              </Box>
            )}
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<PreviewIcon />}
              onClick={() => setShowPreviewModal(true)}
            >
              Preview
            </Button>
            <Button
              variant="solid"
              color="primary"
              startDecorator={submitMutation.isPending ? <CircularProgress size="sm" /> : <CloudUploadIcon />}
              onClick={() => setShowConfirmModal(true)}
              disabled={submitMutation.isPending}
            >
              {status?.has_listing ? 'Sync to Yext' : 'Set Up & Sync'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Modal open={showPreviewModal} onClose={() => setShowPreviewModal(false)}>
        <ModalDialog sx={{ maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Data Preview</Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
            This is the data that will be sent to Yext
          </Typography>

          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : preview ? (
            <Box>
              <Typography level="title-md" sx={{ mb: 1 }}>Organization Info</Typography>
              <List size="sm">
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-xs">Name</Typography>
                    <Typography level="body-md">{preview.organization_data.name}</Typography>
                  </ListItemContent>
                </ListItem>
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-xs">Address</Typography>
                    <Typography level="body-md">{preview.organization_data.address}</Typography>
                  </ListItemContent>
                </ListItem>
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-xs">Phone</Typography>
                    <Typography level="body-md">{preview.organization_data.phone}</Typography>
                  </ListItemContent>
                </ListItem>
                <ListItem>
                  <ListItemContent>
                    <Typography level="body-xs">Website</Typography>
                    <Typography level="body-md">{preview.organization_data.website || 'Not set'}</Typography>
                  </ListItemContent>
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography level="title-md" sx={{ mb: 1 }}>Yext Payload</Typography>
              <Box
                sx={{
                  bgcolor: 'background.level1',
                  p: 2,
                  borderRadius: 'sm',
                  fontFamily: 'monospace',
                  fontSize: 'xs',
                  overflow: 'auto',
                  maxHeight: 300
                }}
              >
                <pre style={{ margin: 0 }}>
                  {JSON.stringify(preview.preview, null, 2)}
                </pre>
              </Box>
            </Box>
          ) : null}
        </ModalDialog>
      </Modal>

      {/* Confirmation Modal */}
      <Modal open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">
            {status?.has_listing ? 'Sync to Yext?' : 'Set Up Yext Listing?'}
          </Typography>
          <Typography level="body-md" sx={{ my: 2 }}>
            {status?.has_listing
              ? 'This will update your business information across 70+ directory sites via Yext. Changes may take up to 5 business days to appear on all sites.'
              : 'This will create a new Yext listing and sync your business information to 70+ directory sites. A Yext subscription is required for distribution.'}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSync}
              loading={submitMutation.isPending || createListingMutation.isPending}
            >
              {status?.has_listing ? 'Sync Now' : 'Set Up & Sync'}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
};
